import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AnalysisCharts from '../components/analysis/AnalysisCharts'
import AnnotationImageViewer from '../components/analysis/AnnotationImageViewer'
import AnnotatorStats from '../components/analysis/AnnotatorStats'
import ConsistencyCharts from '../components/analysis/ConsistencyCharts'
import ExportPanel from '../components/analysis/ExportPanel'
import ProjectNavigation from '../components/projects/ProjectNavigation'
import { projectAPI, analysisAPI } from '../services/api'
import { ProjectAnalysis, Project } from '../types'

export default function AnalysisPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [analysis, setAnalysis] = useState<ProjectAnalysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'consistency' | 'images' | 'annotators' | 'export'
  >('overview')
  const [analysisMode, setAnalysisMode] = useState<'sequential' | 'independent'>('sequential')
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(undefined)
  const [isInitialModeSet, setIsInitialModeSet] = useState(false)

  useEffect(() => {
    if (projectId) {
      fetchProjectAndAnalysis()
    }
  }, [projectId])

  const handleNavigateToImage = (imageId: string) => {
    setSelectedImageId(imageId)
    setActiveTab('images')
  }

  const fetchProjectAndAnalysis = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch project details
      const projectResponse = await projectAPI.get(projectId!)
      setProject(projectResponse.data)

      // Set initial analysis mode based on whether project has video
      if (!isInitialModeSet) {
        const hasVideo =
          projectResponse.data.video_path !== null &&
          projectResponse.data.video_path !== undefined &&
          projectResponse.data.video_status !== 'none'
        setAnalysisMode(hasVideo ? 'sequential' : 'independent')
        setIsInitialModeSet(true)

        // If no video and currently on consistency tab, switch to overview
        if (!hasVideo && activeTab === 'consistency') {
          setActiveTab('overview')
        }
      }

      // Fetch analysis (now includes consistency metrics)
      const analysisResponse = await analysisAPI.getProjectAnalysis(projectId!)
      setAnalysis(analysisResponse.data)
    } catch (err: any) {
      console.error('Error fetching analysis:', err)
      setError('Failed to load analysis data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !project || !analysis) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Failed to load analysis'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/projects')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Projects
          </button>
          <ProjectNavigation projectId={projectId!} activePage="analysis" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900">{project.name} - Analysis</h1>
        <p className="mt-2 text-gray-600">Analyze annotation quality and export data</p>
      </div>

      {/* Analysis Mode Toggle */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Analysis Mode</h3>
            <p className="text-xs text-gray-500 mt-1">
              {analysisMode === 'sequential'
                ? 'For video frames or similar sequential images'
                : 'For unrelated images with multiple objects'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span
              className={`text-sm ${analysisMode === 'sequential' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
            >
              Sequential Images
            </span>
            <button
              onClick={() => {
                const newMode = analysisMode === 'sequential' ? 'independent' : 'sequential'
                setAnalysisMode(newMode)
                // If switching to independent mode while on consistency tab, switch to overview
                if (newMode === 'independent' && activeTab === 'consistency') {
                  setActiveTab('overview')
                }
              }}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  analysisMode === 'independent' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`text-sm ${analysisMode === 'independent' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
            >
              Independent Images
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => {
                setActiveTab('overview')
                setSelectedImageId(undefined)
              }}
              className={`py-2 px-4 text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => analysisMode === 'sequential' && setActiveTab('consistency')}
              disabled={analysisMode === 'independent'}
              className={`ml-8 py-2 px-4 text-sm font-medium ${
                analysisMode === 'independent'
                  ? 'text-gray-300 cursor-not-allowed'
                  : activeTab === 'consistency'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Consistency Analysis
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`ml-8 py-2 px-4 text-sm font-medium ${
                activeTab === 'images'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Image Viewer
            </button>
            <button
              onClick={() => setActiveTab('annotators')}
              className={`ml-8 py-2 px-4 text-sm font-medium ${
                activeTab === 'annotators'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annotator Statistics
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`ml-8 py-2 px-4 text-sm font-medium ${
                activeTab === 'export'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Export Data
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <AnalysisCharts
              statistics={analysis.annotation_statistics || {}}
              annotatorCount={analysis.annotators?.length || 0}
              analysisMode={analysisMode}
              consistencyMetrics={analysis.consistency_metrics || []}
              onNavigateToImage={handleNavigateToImage}
            />
          )}

          {activeTab === 'consistency' && analysisMode === 'sequential' && analysis && (
            <ConsistencyCharts
              metrics={analysis.consistency_metrics || []}
              meanBboxStd={analysis.mean_bbox_std || 0}
              meanVisibilityMetric={analysis.mean_visibility_metric || 0}
              meanKeypointStd={analysis.mean_keypoint_std || 0}
              inferenceStats={analysis.annotation_statistics?.keypoint_inference_stats}
              keypointNames={project?.keypoint_schema}
              analysisMode={analysisMode}
            />
          )}

          {activeTab === 'images' && project && (
            <AnnotationImageViewer
              projectId={projectId!}
              project={project}
              initialImageId={selectedImageId}
            />
          )}

          {activeTab === 'annotators' && <AnnotatorStats projectId={projectId!} />}

          {activeTab === 'export' && <ExportPanel projectId={projectId!} />}
        </div>
      </div>
    </div>
  )
}
